'use client';

import React, { useEffect, useRef, MouseEventHandler } from 'react';
import Image from 'next/image';
import { scrollLock, isScrollUnLocked, scrollUnlock } from '@/utils/scroll';

interface ButtonProps {
  onClick: MouseEventHandler<HTMLButtonElement>;
}

export const MatterportButton = ({ onClick }: ButtonProps) => {
  return (
    <button
      onClick={onClick}
      className="mr-2 mt-2 flex cursor-pointer flex-row rounded-sm bg-grey-6 px-3 py-1.5 font-source-sans text-[14px] font-semibold uppercase leading-8 text-black-standard shadow-component"
    >
      <Image
        src="/public-assets/cube-open.svg"
        alt="Cube"
        height={16}
        width={16}
        className="relative mr-1"
      />
      Take 3d Tour
    </button>
  );
};

const modalStack: React.MutableRefObject<HTMLInputElement | null>[] = [];

interface Props {
  matterportId?: string;
  onClick: MouseEventHandler<HTMLDivElement>;
}

const Matterport = ({ matterportId, onClick }: Props) => {
  const modalContainerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isScrollUnLocked()) {
      scrollLock();
      return () => {
        scrollUnlock();
      };
    }
  }, []);

  // Handle exit using escape key via eventListener. If enter key is processed here for closing modal, we have problems
  // when enter key press is required for other things like a button (e.g. login modal button).
  const handleKeyDown = (e) => {
    if (modalStack[modalStack.length - 1] == modalContainerRef) {
      onClick(e);
    }
  };

  // Handle exit using enter key via onKeyDown.
  const handleExit = (e) => {
    if (e.keyCode === 13) {
      onClick(e);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    modalStack.push(modalContainerRef);

    if (modalContainerRef.current) {
      const focusableElements = modalContainerRef.current.querySelectorAll(
        'button, [href], div, [tabindex]:not([tabindex="-1"])'
      );
      (focusableElements[0] as HTMLElement).focus();
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      modalStack.splice(modalStack.indexOf(modalContainerRef), 1);
    };
  }, [handleKeyDown]);

  return matterportId ? (
    <div
      className="fixed bottom-0 left-0 right-0 top-0 z-[199] h-screen w-screen overscroll-none bg-black-standard bg-opacity-95"
      onClick={onClick}
    >
      <div
        className="mx-auto mt-[5%] h-2/3 w-mobile"
        ref={modalContainerRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="mb-2 flex justify-end"
          onKeyDown={handleExit}
          onClick={onClick}
        >
          <Image
            src="/public-assets/menu/x.svg"
            alt="Close Modal"
            height={24}
            width={24}
            className="cursor-pointer"
            onClick={onClick}
          />
        </div>
        <iframe
          title="Matterport 3D Tour"
          className="h-full w-full"
          allowFullScreen
          allow="xr-spatial-tracking"
          src={`https://my.matterport.com/show/?m=${matterportId}&play=1`}
        />
      </div>
    </div>
  ) : null;
};

export default Matterport;
